import { Computed, Reactive, reactive, useObservable } from '@legendapp/state/react'
import { formOptions, useForm } from '@tanstack/react-form'
import { useRouteContext } from '@tanstack/react-router'
import { zodValidator } from '@tanstack/zod-form-adapter'
import { Check } from 'lucide-react'
import { toast } from 'react-toastify'
import { z } from 'zod'

import { fetchSiteList } from '~features/sites/api'
import { SitesRoot } from '~features/sites/types'
import { Button, Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger, FieldInfo, Input, Label, Popover, PopoverContent, PopoverTrigger } from '~in-app/components'
import { Dialog, DialogContent } from '~in-app/components'
import { auth$ } from '~in-app/legend-state'
import { cn } from '~in-app/lib/utils'

import { generateQR } from '../api'

const formOpts = formOptions({
  defaultValues: {
    siteId: { value: '', label: '' },
    numberOfQR: '',
  },
  validatorAdapter: zodValidator(),
  validators: {
    onChange: z.object({
      siteId: z.object({
        value: z.string(),
        label: z.string(),
      }),
      numberOfQR: z.coerce.number().min(1),
    }),
  },
})

const ReactiveDialog = reactive(Dialog)
const ReactivePopover = reactive(Popover)

export function GenerateQRDialog() {
  const context = useRouteContext({ from: '/console/qr/list' })
  const state$ = useObservable({
    dialogOpen: false,
    siteMenuOpen: false,
    siteList: [] as SitesRoot[],
    fetchSiteList: async () => {
      const fetchAllPages = async (
        page: number = 0,
        accumulated: any[] = [],
      ): Promise<any[]> => {
        const res = await fetchSiteList({
          query: {
            page,
            size: 50, // adjust if needed
          },
        })

        // If API returns null, just stop and return what we have so far
        if (!res) {
          return accumulated
        }

        const list = res.list ?? []
        const merged = [...accumulated, ...list]

        const totalPages = res.totalPage ?? 1
        const currentPage = res.number ?? 0

        if (currentPage < totalPages - 1) {
          return fetchAllPages(page + 1, merged)
        }

        return merged
      }

      // Fetch all pages
      const fullList = await fetchAllPages()

      // Update state with the complete list
      state$.siteList.set(fullList)
    },
  })

  const formControl = useForm({
    ...formOpts,
    onSubmit: async ({ value }) => {
      await generateQR({
        siteId: value.siteId.value,
        numberOfQR: Number(value.numberOfQR),
      })
        .then(() => {
          state$.dialogOpen.set(false)
          toast.success('QR codes generated successfully', { hideProgressBar: false, autoClose: 1500 })
          context.queryClient.invalidateQueries({ queryKey: ['qr-listing'] })
        })
        .catch((err) => {
          toast.error(err.message, { hideProgressBar: false, autoClose: 1500 })
        })
    },
  })

  return (
    <ReactiveDialog
      $open={state$.dialogOpen}
      onOpenChange={
        (value) => {
          state$.dialogOpen.set(value)
          if (value) {
            state$.fetchSiteList()
          }
        }
      }
    >
      <DialogTrigger asChild>
        <Button
          color="muted"
          disabled={!auth$.privileges.get()?.has('0.3.1.12')}
          size="medium"
          variant="outlined"
        >
          Generate
        </Button>
      </DialogTrigger>

      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Generate QR</DialogTitle>

          <DialogDescription>
            Please fill in the site and the number of QR codes you want to generate.
          </DialogDescription>
        </DialogHeader>

        <form
          noValidate
          autoComplete="off"
          autoFocus={false}
          className="grid gap-4 py-4"
          onSubmit={formControl.handleSubmit}
        >
          <div className="grid gap-2">
            <Label
              required
              htmlFor="siteId"
            >
              Site
            </Label>

            <ReactivePopover
              $open={state$.siteMenuOpen}
              onOpenChange={
                (value) => {
                  state$.siteMenuOpen.set(value)
                }
              }
            >
              <PopoverTrigger>
                <formControl.Subscribe
                  selector={state => state.values.siteId}
                  children={
                    ({ label, value }) => (
                      <Reactive.div
                        $className={
                          () => cn(
                            'flex h-10 w-full items-center justify-start rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
                            state$.siteMenuOpen.get() && 'ring-2 ring-ring ring-offset-2',
                          )
                        }
                      >
                        {label || 'Select a site...'}
                      </Reactive.div>
                    )
                  }
                />
              </PopoverTrigger>

              <PopoverContent
                align="start"
                className="w-[300px] p-0"
              >
                <Command>
                  <CommandInput placeholder="Search site" />

                  <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    <CommandList>
                      <CommandEmpty>No site found.</CommandEmpty>

                      <Computed>
                        {
                          () => (
                            <CommandGroup>
                              {
                                state$.siteList.get().map(site => (
                                  <CommandItem
                                    key={site.id}
                                    onSelect={
                                      () => {
                                        state$.siteMenuOpen.set(false)
                                        formControl.setFieldValue('siteId', { value: site.id, label: site.name })
                                      }
                                    }
                                  >
                                    <Check
                                      className={
                                        cn(
                                          'mr-2 h-4 w-4',
                                          formControl.state.values.siteId.value === site.id ? 'opacity-100' : 'opacity-0',
                                        )
                                      }
                                    />

                                    {site.name}
                                  </CommandItem>
                                ))
                              }
                            </CommandGroup>
                          )
                        }
                      </Computed>
                    </CommandList>
                  </div>
                </Command>
              </PopoverContent>
            </ReactivePopover>
          </div>

          <div className="grid gap-2">
            <Label
              required
              htmlFor="numberOfQR"
            >
              Number of QR
            </Label>

            <formControl.Field
              name="numberOfQR"
              children={
                field => (
                  <>
                    <Input
                      id="numberOfQR"
                      type="number"
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={e => field.handleChange(e.target.value)}
                    />

                    <FieldInfo field={field} />
                  </>
                )
              }
            />
          </div>
        </form>

        <DialogFooter>
          <formControl.Subscribe
            selector={state => [state.canSubmit, state.isSubmitting]}
            children={
              ([canSubmit, isSubmitting]) => (
                <Button
                  disabled={!canSubmit}
                  type="submit"
                  onClick={formControl.handleSubmit}
                >
                  {isSubmitting ? 'Generating...' : 'Generate'}
                </Button>
              )
            }
          />
        </DialogFooter>
      </DialogContent>
    </ReactiveDialog>
  )
}
